import React from 'react';
import { motion } from 'framer-motion';
import { PlayIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';

const COG_VARS = {
  '--cog-blue-1': '#277AB2',
  '--cog-blue-2': '#0B5996',
  '--cog-blue-3': '#082B4A',
};

function DottedOrbit({ className, size = 520 }) {
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 600 600" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <defs>
        <radialGradient id="productHeroOrbGlow" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(300 300) rotate(90) scale(280)">
          <stop stopColor="#38BDF8" stopOpacity="0.18" />
          <stop offset="0.5" stopColor="#34D399" stopOpacity="0.10" />
          <stop offset="1" stopColor="#6366F1" stopOpacity="0" />
        </radialGradient>
        <pattern id="productHeroDots" x="0" y="0" width="18" height="18" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="1" fill="#0F172A" fillOpacity="0.10" />
        </pattern>
      </defs>
      <circle cx="300" cy="300" r="280" fill="url(#productHeroOrbGlow)" />
      <circle cx="300" cy="300" r="240" stroke="#0F172A" strokeOpacity="0.10" strokeWidth="2" />
      <circle cx="300" cy="300" r="170" stroke="#0F172A" strokeOpacity="0.08" strokeWidth="2" strokeDasharray="3 9" />
      <circle cx="300" cy="300" r="110" stroke="#0F172A" strokeOpacity="0.08" strokeWidth="2" strokeDasharray="2 10" />
      <circle cx="300" cy="300" r="245" fill="url(#productHeroDots)" fillOpacity="0.35" />
      {[
        { x: 300, y: 60, c: '#277AB2' },
        { x: 520, y: 300, c: '#0B5996' },
        { x: 300, y: 540, c: '#277AB2' },
        { x: 80, y: 300, c: '#0B5996' },
      ].map((n, i) => (
        <g key={i}>
          <circle cx={n.x} cy={n.y} r={10} fill={n.c} fillOpacity="0.20" />
          <circle cx={n.x} cy={n.y} r={4} fill={n.c} fillOpacity="0.65" />
        </g>
      ))}
    </svg>
  );
}

function SubtleNoise({ className }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" preserveAspectRatio="none" aria-hidden>
      <filter id="productHeroNoise">
        <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="3" stitchTiles="stitch" />
        <feColorMatrix type="saturate" values="0" />
        <feComponentTransfer>
          <feFuncA type="table" tableValues="0 0.22" />
        </feComponentTransfer>
      </filter>
      <rect width="100%" height="100%" filter="url(#productHeroNoise)" opacity="0.22" />
    </svg>
  );
}

function Glass({ children, className = '' }) {
  return (
    <div
      className={
        'rounded-3xl border border-white/15 bg-white/12 shadow-[0_10px_40px_-20px_rgba(2,6,23,0.45)] backdrop-blur ' + (className || '')
      }
    >
      {children}
    </div>
  );
}

/**
 * Landing-style hero for product pages. Track 'bcg' | 'mck' drives gradient.
 * eyebrowChips: string[]
 * proofRow: { label?: string, value: string }[]
 * previewChildren: React node for the right-side glass panel (product-specific preview).
 */
export default function LandingStyleHero({
  track,
  headline,
  subheadline,
  eyebrowChips,
  proofRow,
  primaryCtaLabel,
  onPrimaryCta,
  secondaryCtaLabel,
  onSecondaryCta,
  previewChildren,
  announcement,
}) {
  const heroBg =
    track === 'mck'
      ? 'bg-[linear-gradient(135deg,var(--cog-blue-1)_0%,var(--cog-blue-2)_55%,var(--cog-blue-3)_100%)]'
      : 'bg-[linear-gradient(135deg,var(--cog-blue-1)_0%,#10b981_40%,var(--cog-blue-2)_100%)]';

  const previewShellClass =
    'relative overflow-hidden border-white/20 ring-1 ring-white/10 shadow-[0_22px_60px_-26px_rgba(2,6,23,0.68)]' +
    (track === 'bcg' ? ' bg-white/10' : ' bg-white/16');

  return (
    <section className="relative overflow-hidden" style={COG_VARS}>
      <style>{`
        .product-hero-shimmer { position: relative; overflow: hidden; }
        .product-hero-shimmer::after {
          content: '';
          position: absolute;
          width: 50%;
          height: 200%;
          top: -50%;
          left: -50%;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%);
          transform: rotate(-35deg);
          animation: productHeroShine 5s ease-in-out infinite;
          pointer-events: none;
          z-index: 1;
        }
        @keyframes productHeroShine {
          0% { transform: rotate(-35deg) translate(-100%, -100%); }
          18% { transform: rotate(-35deg) translate(250%, 250%); }
          100% { transform: rotate(-35deg) translate(250%, 250%); }
        }
      `}</style>
      <div className={`absolute inset-0 opacity-95 ${heroBg}`} />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(255,255,255,0.25),transparent_45%),radial-gradient(circle_at_80%_20%,rgba(255,255,255,0.18),transparent_45%),radial-gradient(circle_at_50%_95%,rgba(255,255,255,0.10),transparent_45%)]" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.10] [background-image:radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] [background-size:22px_22px]" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.20]">
        <SubtleNoise className="h-full w-full" />
      </div>
      <motion.div
        aria-hidden
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8 }}
        className="pointer-events-none absolute -right-56 top-10 hidden md:block"
      >
        <DottedOrbit className="opacity-[0.85]" />
      </motion.div>

      <div className="relative w-full max-w-[99%] ml-[1%] mr-0 px-4 sm:px-8 md:px-16 pt-12 sm:pt-16 md:pt-20 lg:pt-24 pb-20 sm:pb-28 md:pb-36 lg:pb-40">
        <div className="grid items-stretch gap-8 sm:gap-12 md:grid-cols-2 md:gap-16 lg:gap-24">
          <div className="text-white min-h-[280px] sm:min-h-[320px] md:min-h-[420px] h-full flex flex-col justify-center">
            <div className="flex flex-wrap items-center gap-2">
              {eyebrowChips.map((chip, i) => (
                <span key={i} className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold">
                  {chip}
                </span>
              ))}
            </div>
            {announcement ? <div className="mt-5">{announcement}</div> : null}
            <h1 className="mt-8 text-balance text-4xl font-black leading-[1.05] tracking-tight md:text-6xl">{headline}</h1>
            <div className="product-hero-shimmer mt-7 max-w-prose relative overflow-hidden">
              <p className="relative z-0 max-w-prose text-pretty text-base font-bold leading-relaxed text-white/95 md:text-xl md:leading-relaxed">
                {subheadline}
              </p>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={onPrimaryCta}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold rounded-xl text-white shadow-md hover:shadow-xl transform hover:scale-105 transition-all duration-300 active:scale-[0.99] bg-gradient-to-r from-blue-600 to-blue-400"
              >
                {primaryCtaLabel} <PlayIcon className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={onSecondaryCta}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold rounded-2xl bg-white/10 text-white border border-white/15 hover:bg-white/15 transition-all duration-300 active:scale-[0.99]"
              >
                {secondaryCtaLabel}
              </button>
              <span className="text-xs font-semibold text-white/70">
                <CheckBadgeIcon className="w-3.5 h-3.5 mr-1 inline" /> Cognidoor
              </span>
            </div>
            <div className="mt-8 sm:mt-12 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {proofRow.map((item, i) => (
                <Glass key={i} className="flex min-h-[88px] min-w-0 flex-col items-center justify-center p-5 text-center sm:min-h-[92px]">
                  {item.label ? <div className="text-xs font-bold text-white/70">{item.label}</div> : null}
                  <div className={(item.label ? 'mt-2 text-lg font-extrabold' : 'text-lg font-extrabold') + ' min-w-0 leading-snug'}>
                    {item.value}
                  </div>
                </Glass>
              ))}
            </div>
          </div>
          <div className="flex flex-col items-stretch min-h-[280px] sm:min-h-[320px] md:min-h-[420px] h-full justify-center">
            <Glass className={previewShellClass}>{previewChildren}</Glass>
          </div>
        </div>
      </div>
      <div className="relative h-16 bg-white">
        <div className="absolute -top-14 left-0 right-0 h-20 bg-white [clip-path:polygon(0%_65%,100%_0%,100%_100%,0%_100%)]" />
      </div>
    </section>
  );
}
